import React, { useState, useEffect, useRef } from 'react';
import { Navigation } from './components/Navigation';
import { ShoppingListScreen } from './components/ShoppingListScreen';
import { RecipeScreen } from './components/RecipeScreen';
import { SettingsScreen } from './components/SettingsScreen';
import { OnboardingScreen } from './components/OnboardingScreen';
import { CoachScreen } from './components/CoachScreen';
import { SharedRecipeScreen } from './components/SharedRecipeScreen';
import { ReceiveSharedListModal } from './components/ReceiveSharedListModal';
import { ShoppingItem, Recipe, ViewState, Ingredient, User, ShoppingList, Invitation } from './types';
import { parseRecipeFromImage, generateRecipeImage, categorizeItems, getInstantCategory, getCoachRecommendation, getCoachRecommendationFromPhoto, refineCoachRecipe } from './services/geminiService';
import { 
    subscribeToData, 
    saveListToCloud, 
    deleteListFromCloud,
    saveRecipeToCloud, 
    deleteRecipeFromCloud,
    loadInitialData,
    subscribeToInvitations,
    respondToInvitation,
    resolveSharedRecipeToken,
    parseSharedRecipeToken,
    fetchPublicRecipe,
    decodeSharedListData,
    SharedListData,
    recordKnownUser,
    persistUserSession,
    getPersistedUserSession,
    clearPersistedUserSession,
    savePendingSharedList,
    getPendingSharedList,
    clearPendingSharedList,
    savePendingSharedRecipe,
    getPendingSharedRecipe,
    clearPendingSharedRecipe,
    isStandaloneApp,
    migrateLegacyLocalUser,
    joinSharedList,
    canSyncToCloud
} from './services/storageService';
import { isFirebaseReady, auth } from './services/firebase';
import { onAuthStateChanged, signOut, getRedirectResult } from 'firebase/auth';

const INITIAL_LIST: ShoppingList = {
    id: 'default-1',
    name: 'Boodschappen',
    items: [
        { id: '1', name: 'Volle melk', quantity: '1L', checked: false },
        { id: '2', name: 'Bruin brood', quantity: '1 stuks', checked: false },
    ],
    sharedWith: [],
    accessIds: []
};

const INITIAL_RECIPES: Recipe[] = [
    {
        id: '1',
        title: 'Pasta Carbonara',
        image: 'https://images.unsplash.com/photo-1612874742237-6526221588e3?auto=format&fit=crop&q=80&w=800',
        time: '20 min',
        calories: '650 kcal',
        category: 'Diner',
        isFavorite: true,
        ingredients: [
            { name: 'Spaghetti', amount: '400g' },
            { name: 'Pancetta', amount: '150g' },
            { name: 'Eieren', amount: '4 stuks' },
            { name: 'Pecorino Romano', amount: '100g' },
            { name: 'Zwarte peper', amount: 'naar smaak' }
        ],
        steps: [
            "Breng een grote pan water aan de kook voor de pasta. Voeg flink wat zout toe zodra het water kookt.",
            "Snijd de pancetta in reepjes. Bak ze in een droge koekenpan op middelhoog vuur krokant en goudbruin.",
            "Klop ondertussen de eieren los in een kom en meng de geraspte Pecorino Romano en een royale hoeveelheid zwarte peper erdoor.",
            "Kook de spaghetti al dente. Giet af maar bewaar een kopje kookvocht. Meng de pasta direct met het spek en daarna, van het vuur af, met het eimengsel."
        ],
        status: 'ready'
    }
];

export type ProcessingStatus = 'idle' | 'uploading' | 'analyzing' | 'generating_image' | 'saving' | 'completed' | 'error';

export default function App() {
  const [currentView, setCurrentView] = useState<ViewState>('list');
  const [activeRecipeId, setActiveRecipeId] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // User State
  const [user, setUser] = useState<User | null>(null);
  
  // Data State
  const [shoppingLists, setShoppingLists] = useState<ShoppingList[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [activeListId, setActiveListId] = useState<string>('');
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Sync State
  const [lastSynced, setLastSynced] = useState<Date>(new Date());
  const [notification, setNotification] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>('idle');

  // Invitation State
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // COACH STATE
  const [coachInventory, setCoachInventory] = useState("");
  const [isCoachAnalyzing, setIsCoachAnalyzing] = useState(false);
  const [coachRecommendation, setCoachRecommendation] = useState<any>(null);
  const [coachAddedIngredients, setCoachAddedIngredients] = useState<string[]>([]);
  const [isCoachRefining, setIsCoachRefining] = useState(false);

  // SHARED RECIPE STATE
  const [sharedRecipe, setSharedRecipe] = useState<Recipe | null>(null);
  const [sharedRecipeToken, setSharedRecipeToken] = useState<string | null>(null);
  const [isImportingShared, setIsImportingShared] = useState(false);

  // SHARED LIST STATE (WHATSAPP INVITES)
  const [receivedSharedList, setReceivedSharedList] = useState<SharedListData | null>(null);
  const [showReceiveSharedListModal, setShowReceiveSharedListModal] = useState(false);

  // Capture initial URL state immediately to avoid race conditions with cleanup
  const initialSearchParams = useRef(new URLSearchParams(window.location.search));

  const userRef = useRef(user);
  const pendingActiveListIdRef = useRef<string | null>(null);

  // Meldingen als iemand anders een gedeelde lijst aanpast (alleen terwijl de app open of op de achtergrond is)
  const lastSeenListVersion = useRef<Record<string, number> | null>(null);
  const notifyRemoteListChanges = (lists: ShoppingList[]) => {
      const me = userRef.current;
      const previous = lastSeenListVersion.current;
      const next: Record<string, number> = {};
      lists.forEach(l => { next[l.id] = l.lastModified || 0; });
      lastSeenListVersion.current = next;
      if (!previous || !me) return; // eerste keer laden: geen meldingen

      lists.forEach(l => {
          const changed = (l.lastModified || 0) > (previous[l.id] || 0) && previous[l.id] !== undefined;
          if (!changed || !l.lastModifiedById || l.lastModifiedById === me.id) return;
          const message = `${l.lastModifiedBy || 'Iemand'} heeft "${l.name}" aangepast`;
          if (document.visibilityState === 'visible') {
              showNotification(message);
          } else if ('Notification' in window && Notification.permission === 'granted') {
              navigator.serviceWorker?.getRegistration().then(reg => {
                  reg?.showNotification('superMarct', { body: message, tag: `list-${l.id}`, icon: 'https://cdn-icons-png.flaticon.com/512/2203/2203183.png' });
              }).catch(() => {});
          }
      });
  };
  const activeListIdRef = useRef(activeListId);

  useEffect(() => {
    userRef.current = user;
    activeListIdRef.current = activeListId;
  }, [user, activeListId]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
        if (event.state) {
            setCurrentView(event.state.view);
            setActiveRecipeId(event.state.recipeId);
            if (event.state.view !== 'shared-recipe') {
                setSharedRecipe(null);
            }
        }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateTo = (view: ViewState, recipeId: string | null = null) => {
      window.history.pushState({ view, recipeId }, '');
      setCurrentView(view);
      setActiveRecipeId(recipeId);
  };

  // Process incoming deep link URL or search params
  const lastHandledShare = useRef<string>('');

  const openSharedRecipeToken = (token: string) => {
    savePendingSharedRecipe(token);
    resolveSharedRecipeToken(token).then(recipe => {
      if (recipe) {
        setSharedRecipe(recipe);
        setSharedRecipeToken(token);
        setCurrentView('shared-recipe');
        clearPendingSharedRecipe();

        // Link bevat receptdata én een Firestore id: haal op de achtergrond de versie mét foto op
        const { shareId, data } = parseSharedRecipeToken(token);
        if (shareId && data) {
          fetchPublicRecipe(shareId).then(full => {
            if (full) setSharedRecipe(prev => (prev && prev.title === full.title ? { ...prev, image: full.image || prev.image } : prev));
          });
        }
      } else {
        clearPendingSharedRecipe();
        showNotification("Gedeeld recept niet gevonden.");
      }
    });
  };

  const openSharedListRaw = (raw: string): boolean => {
    const decoded = decodeSharedListData(raw);
    if (decoded) {
      setReceivedSharedList(decoded);
      setShowReceiveSharedListModal(true);
      clearPendingSharedList();
      return true;
    }
    return false;
  };

  /** Returns true als er een gedeeld recept of gedeelde lijst in de link zat. */
  const handleIncomingShare = (urlOrQuery: string): boolean => {
    try {
      let params: URLSearchParams;
      if (urlOrQuery.startsWith('http://') || urlOrQuery.startsWith('https://')) {
        params = new URL(urlOrQuery).searchParams;
      } else if (urlOrQuery.startsWith('web+supermarct:')) {
        const raw = urlOrQuery.replace(/^web\+supermarct:(\/\/)?(open)?\/?\??/, '');
        params = new URLSearchParams(raw);
      } else if (urlOrQuery.startsWith('?')) {
        params = new URLSearchParams(urlOrQuery);
      } else {
        params = new URL(urlOrQuery, window.location.origin).searchParams;
      }

      // Handle custom protocol deeplink parameter wrapper
      if (params.has('deeplink')) {
        const nested = params.get('deeplink') || '';
        if (nested) {
          return handleIncomingShare(nested);
        }
      }

      // Voorkom dubbele verwerking (bv. URL bij opstart + launchQueue met dezelfde URL)
      const key = params.toString();
      if (key && key === lastHandledShare.current) return true;

      let handled = false;

      // 1. Shared Recipe (Firestore id of receptdata in de link)
      const shareId = params.get('share_id');
      const shareRecipeData = params.get('share_recipe_data');
      if (shareId || shareRecipeData) {
        const tokenParams = new URLSearchParams();
        if (shareId) tokenParams.set('share_id', shareId);
        if (shareRecipeData) tokenParams.set('share_recipe_data', shareRecipeData);
        openSharedRecipeToken(tokenParams.toString());
        handled = true;
      }

      // 2. Shared Shopping List
      const shareListDataRaw = params.get('share_list_data');
      if (shareListDataRaw) {
        savePendingSharedList(shareListDataRaw);
        if (!openSharedListRaw(shareListDataRaw)) {
          clearPendingSharedList();
          showNotification("Gedeelde boodschappenlijst kon niet worden geladen.");
        }
        handled = true;
      }

      if (handled) lastHandledShare.current = key;
      return handled;
    } catch (e) {
      console.error("Deep link processing error:", e);
      return false;
    }
  };

  // Open een gedeelde link die de gebruiker heeft gekopieerd (vooral voor iPhone, waar
  // links nooit direct in de beginscherm-app openen).
  const handleOpenSharedLinkFromClipboard = async () => {
    let text = '';
    try {
      text = (await navigator.clipboard.readText()).trim();
    } catch (e) {
      text = (window.prompt("Plak hier de gedeelde superMarct link:") || '').trim();
    }
    const match = text.match(/(https?:\/\/\S+|\?\S+)/);
    if (!match || !handleIncomingShare(match[1])) {
      showNotification("Geen gedeelde superMarct link gevonden op je klembord.");
    }
  };

  const checkPendingShares = () => {
    const pendingList = getPendingSharedList();
    if (pendingList && !openSharedListRaw(pendingList)) {
      clearPendingSharedList();
    }
    const pendingRecipe = getPendingSharedRecipe();
    if (pendingRecipe) {
      openSharedRecipeToken(pendingRecipe);
    }
  };

  // Check for shared links on launch, deep links via PWA launchQueue, and pending shares from browser
  useEffect(() => {
    // 1. URL search parameters on boot
    if (window.location.search) {
      handleIncomingShare(window.location.search);
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      // 2. Check pending shared items (e.g. user clicked WhatsApp link in the browser, then opened installed app)
      checkPendingShares();
    }

    // 3. Chromium PWA Launch Queue API: links die een al geopende app hergebruiken (launch_handler)
    if ('launchQueue' in window && 'setConsumer' in (window as any).launchQueue) {
      try {
        (window as any).launchQueue.setConsumer((launchParams: { targetURL?: string }) => {
          if (launchParams.targetURL) {
            handleIncomingShare(launchParams.targetURL);
          }
        });
      } catch (err) {
        console.warn("launchQueue setup:", err);
      }
    }

    // 4. Check pending shares when the app comes back to the foreground
    const handleFocusOrVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkPendingShares();
      }
    };

    window.addEventListener('visibilitychange', handleFocusOrVisibility);
    window.addEventListener('focus', handleFocusOrVisibility);

    return () => {
      window.removeEventListener('visibilitychange', handleFocusOrVisibility);
      window.removeEventListener('focus', handleFocusOrVisibility);
    };
  }, []);

  // Auth & Initial Data Load
  useEffect(() => {
      // 1. Initial Local Load (for speed & permanent session persistence)
      const savedUser = getPersistedUserSession();
      const hasOnboarded = localStorage.getItem('supermarct-onboarding-completed');
      const startedWithShareLink = initialSearchParams.current.has('share_id') || initialSearchParams.current.has('share_recipe_data') || initialSearchParams.current.has('share_list_data');

      if (savedUser) {
          // User is authenticated: ensure onboarding is bypassed and load their data
          localStorage.setItem('supermarct-onboarding-completed', 'true');
          setShowOnboarding(false);
          setUser(savedUser);
          const userData = loadInitialData(savedUser.id);
          setShoppingLists(userData.lists);
          setRecipes(userData.recipes);
          setActiveListId(userData.lists[0]?.id || '');
          setIsDataLoaded(true);
      } else {
          if (!hasOnboarded && !startedWithShareLink) {
              setShowOnboarding(true);
          }
          // Load guest data initially
          const guestData = loadInitialData();
          setShoppingLists(guestData.lists);
          setRecipes(guestData.recipes);
          setActiveListId(guestData.lists[0]?.id || '');
          setIsDataLoaded(true);
      }

      // 2. Firebase Auth Listener: sync with Firebase account if active, but NEVER wipe a local Google session
      let unsubscribeAuth = () => {};
      if (auth && isFirebaseReady()) {
          unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
              if (firebaseUser) {
                  const appUser: User = {
                      id: firebaseUser.uid,
                      name: firebaseUser.displayName || 'Gebruiker',
                      email: (firebaseUser.email || '').toLowerCase(),
                      photoUrl: firebaseUser.photoURL || undefined
                  };
                  migrateLegacyLocalUser(appUser);
                  setUser(appUser);
                  persistUserSession(appUser);
                  recordKnownUser(appUser);
              }
              // NOTE: If firebaseUser is null, we deliberately DO NOT log out the user.
              // The user is authenticated with their Google account via the app's persistent session.
              // Logout only occurs when the user explicitly triggers handleLogout().
          });

          // Fouten na een redirect-login (fallback als popup geblokkeerd is) zichtbaar maken
          getRedirectResult(auth).catch((err) => {
              console.error("Google redirect login failed", err);
              showNotification("Inloggen met Google mislukt. Probeer het opnieuw.");
          });
      }

      return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
      const unsubscribe = subscribeToData(user, (data) => {
          setShoppingLists(data.lists);
          notifyRemoteListChanges(data.lists);
          if (data.lists.length > 0) {
              // Net deelgenomen aan een lijst? Die openen zodra hij binnenkomt
              const pendingId = pendingActiveListIdRef.current;
              if (pendingId && data.lists.some(l => l.id === pendingId)) {
                  pendingActiveListIdRef.current = null;
                  setActiveListId(pendingId);
              } else if (!pendingId) {
                  const currentExists = data.lists.find(l => l.id === activeListIdRef.current);
                  if (!currentExists) {
                      setActiveListId(data.lists[0].id);
                  }
              }
          }
          if (data.recipes.length === 0 && !userRef.current) {
              setRecipes(INITIAL_RECIPES);
          } else {
              setRecipes(data.recipes);
          }
          setLastSynced(new Date());
          setIsDataLoaded(true);
      });
      return () => unsubscribe();
  }, [user]);

  // Listen for Invitations
  useEffect(() => {
      if (user?.email) {
          const unsub = subscribeToInvitations(user.email, (invites) => {
              setInvitations(invites);
              if (invites.length > 0) setShowInviteModal(true);
          });
          return () => unsub();
      }
  }, [user?.email]);

  const showNotification = (message: string) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 4000);
  };

  const handleLogin = (newUser: User) => {
      migrateLegacyLocalUser(newUser);
      persistUserSession(newUser);
      recordKnownUser(newUser);
      setUser(newUser);
      showNotification(`Welkom terug, ${newUser.name}`);
      
      // Check for pending shared recipe import
      const pendingImport = localStorage.getItem('pending-shared-recipe');
      if (pendingImport) {
          try {
              const recipe = JSON.parse(pendingImport);
              handleImportSharedRecipe(recipe, newUser);
              localStorage.removeItem('pending-shared-recipe');
          } catch(e) { console.error(e); }
      }
  };

  const handleLogout = async () => {
      if (auth) {
          try {
              await signOut(auth);
          } catch (e) {
              console.warn("SignOut error:", e);
          }
      }
      clearPersistedUserSession();
      setUser(null);
      showNotification('Uitgelogd');
      
      // Load guest data
      const guestData = loadInitialData();
      setShoppingLists(guestData.lists);
      setRecipes(guestData.recipes);
      setActiveListId(guestData.lists[0]?.id || '');
  };

  const getActiveList = () => shoppingLists.find(l => l.id === activeListId) || shoppingLists[0] || INITIAL_LIST;

  const updateListInCloud = (updatedList: ShoppingList) => {
      setShoppingLists(prev => prev.map(l => l.id === updatedList.id ? updatedList : l));
      saveListToCloud(user, updatedList);
  };

  // --- ACCEPT & OPEN RECEIVED SHARED LIST ---
  const handleAcceptSharedList = async (sharedData: SharedListData) => {
      // 1. Live meedoen: de link bevat een deelsleutel en je bent ingelogd
      if (sharedData.token && user && canSyncToCloud(user)) {
          setShowReceiveSharedListModal(false);
          setReceivedSharedList(null);
          pendingActiveListIdRef.current = sharedData.id;
          const joined = await joinSharedList(user, sharedData.id, sharedData.token);
          if (joined) {
              // De lijst komt via de live-synchronisatie binnen en wordt dan geopend
              if (shoppingLists.some(l => l.id === sharedData.id)) {
                  pendingActiveListIdRef.current = null;
                  setActiveListId(sharedData.id);
              }
              setCurrentView('list');
              showNotification(`Je doet nu live mee met "${sharedData.name}"!`);
              return;
          }
          pendingActiveListIdRef.current = null;
          showNotification("Live meedoen lukte niet. Je krijgt een kopie van de lijst.");
      }

      // 2. Kopie van de lijst (geen sleutel, niet ingelogd of deelnemen mislukt)
      const existingList = shoppingLists.find(l => l.id === sharedData.id);

      if (existingList) {
          const existingNames = new Set(existingList.items.map(i => i.name.toLowerCase().trim()));
          const newItems = (sharedData.items || []).filter(i => !existingNames.has(i.name.toLowerCase().trim()));
          
          const updatedList: ShoppingList = {
              ...existingList,
              items: [...newItems, ...existingList.items],
              sharedWith: existingList.sharedWith || []
          };
          updateListInCloud(updatedList);
          setActiveListId(updatedList.id);
          setCurrentView('list');
          showNotification(`Lijst "${updatedList.name}" geopend en bijgewerkt!`);
      } else {
          const newList: ShoppingList = {
              // Eigen id: een kopie mag niet botsen met de originele lijst in de cloud
              id: `shared-list-${Date.now()}`,
              name: sharedData.name,
              items: (sharedData.items || []).map((item, idx) => ({
                  id: item.id || `item-${Date.now()}-${idx}`,
                  name: item.name,
                  quantity: item.quantity,
                  checked: item.checked || false,
                  category: item.category || 'Overig'
              })),
              ownerName: sharedData.ownerName,
              ownerId: user ? user.id : 'shared-owner',
              sharedWith: [],
              accessIds: user ? [user.id] : []
          };

          const updatedLists = [newList, ...shoppingLists];
          setShoppingLists(updatedLists);
          saveListToCloud(user, newList);
          setActiveListId(newList.id);
          setCurrentView('list');
          showNotification(user
              ? `Kopie van "${newList.name}" toegevoegd. Vraag om een nieuwe deellink om live samen te werken.`
              : `Kopie van "${newList.name}" toegevoegd. Log in om live samen te werken.`);
      }

      setShowReceiveSharedListModal(false);
      setReceivedSharedList(null);
  };

  // --- IMPORT SHARED RECIPE LOGIC ---
  const handleImportSharedRecipe = async (recipe: Recipe, activeUser: User | null) => {
      if (!activeUser) {
          // If not logged in, store temporarily and show onboarding/login
          localStorage.setItem('pending-shared-recipe', JSON.stringify(recipe));
          setShowOnboarding(true);
          return;
      }

      setIsImportingShared(true);
      try {
          const newId = `imported-${Date.now()}`;
          const importedRecipe: Recipe = {
              ...recipe,
              id: newId,
              isFavorite: false,
              // We keep originalAuthor for display, but user owns this copy
          };
          
          await saveRecipeToCloud(activeUser, importedRecipe);
          
          // Switch view
          setRecipes(prev => [importedRecipe, ...prev]);
          setSharedRecipe(null);
          navigateTo('recipes');
          setActiveRecipeId(newId);
          setCurrentView('recipe-detail');
          showNotification("Recept toegevoegd aan je collectie!");
      } catch (e) {
          showNotification("Fout bij importeren.");
          console.error(e);
      } finally {
          setIsImportingShared(false);
      }
  };

  // --- COACH ACTIONS ---

  const handleCoachAnalyze = async () => {
      if (!coachInventory.trim()) return;
      setIsCoachAnalyzing(true);
      setCoachRecommendation(null);
      setCoachAddedIngredients([]);
      try {
          const result = await getCoachRecommendation(coachInventory, recipes);
          if (result) {
              if (result.type === 'new' && result.newRecipe) {
                  result.newRecipe.image = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=800&q=80";
                  result.newRecipe.id = `coach-temp-${Date.now()}`;
              }
              setCoachRecommendation(result);
              showNotification("Coach heeft een recept gevonden!");
          }
      } catch (e) {
          showNotification("Coach is even in de war, probeer het later.");
      } finally {
          setIsCoachAnalyzing(false);
      }
  };

  const handleCoachPhotoAnalyze = async (base64Image: string) => {
      setIsCoachAnalyzing(true);
      setCoachRecommendation(null);
      setCoachAddedIngredients([]);
      
      try {
          const result = await getCoachRecommendationFromPhoto(base64Image, recipes);
          if (result) {
              if (result.type === 'new' && result.newRecipe) {
                  result.newRecipe.image = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=800&q=80";
                  result.newRecipe.id = `coach-temp-${Date.now()}`;
              }
              setCoachRecommendation(result);
              showNotification("Coach heeft de foto geanalyseerd!");
          } else {
              showNotification("Kon niets herkennen op de foto.");
          }
      } catch (e) {
          showNotification("Fout bij analyseren van foto.");
      } finally {
          setIsCoachAnalyzing(false);
      }
  };

  const handleCoachRefine = async (customPrompt?: string, silent: boolean = false) => {
      if (!coachRecommendation?.newRecipe) return;
      if (!silent) setIsCoachRefining(true);
      
      try {
          const result = await refineCoachRecipe(
              coachRecommendation.newRecipe, 
              customPrompt || "Werk het recept bij.", 
              coachAddedIngredients
          );
          if (result) {
              setCoachRecommendation((prev: any) => ({
                  ...prev,
                  newRecipe: {
                      ...result.newRecipe,
                      image: prev.newRecipe.image,
                      id: prev.newRecipe.id
                  },
                  missingIngredients: result.missingIngredients || prev.missingIngredients,
                  coachTip: result.coachTip || prev.coachTip
              }));
              if (!silent) {
                  showNotification("Recept bijgewerkt!");
              }
          }
      } catch (e) {
          if (!silent) showNotification("Fout bij bijwerken.");
      } finally {
          if (!silent) setIsCoachRefining(false);
      }
  };

  const handleCoachAddIngredient = async (ing: Ingredient) => {
      const list = getActiveList();
      const currentItems = [...list.items];
      const normalizedName = ing.name.trim().toLowerCase();
      const existingIdx = currentItems.findIndex(i => i.name.trim().toLowerCase() === normalizedName);
      
      if (existingIdx !== -1) {
          currentItems[existingIdx] = { ...currentItems[existingIdx], quantity: ing.amount, checked: false, checkedAt: undefined };
      } else {
           currentItems.unshift({ id: Math.random().toString(), name: ing.name, quantity: ing.amount, checked: false });
      }
      updateListInCloud({ ...list, items: currentItems });
      
      const newAdded = [...coachAddedIngredients, ing.name];
      setCoachAddedIngredients(newAdded);
      
      showNotification(`${ing.name} toegevoegd! Coach past recept aan...`);

      handleCoachRefine(`Ik heb nu ook ${ing.name}. Verwerk dit in de bereidingsstappen.`, true);
  };

  // --- GENERAL ACTIONS ---

  const handleProcessImage = async (base64: string) => {
      if (!user) {
          showNotification("Log in om recepten te scannen");
          return;
      }

      setProcessingStatus('analyzing');
      showNotification("Recept wordt gescand...");
      
      const tempId = `temp-${Date.now()}`;
      const placeholder: Recipe = {
          id: tempId,
          title: "Scan resultaat...",
          image: base64,
          time: "...",
          category: "Diner",
          ingredients: [],
          steps: [],
          isFavorite: false,
          status: 'processing'
      };
      
      setRecipes(prev => [placeholder, ...prev]);
      navigateTo('recipes');

      try {
          const result = await parseRecipeFromImage(base64);
          if (result) {
              setRecipes(prev => prev.filter(r => r.id !== tempId));
              await saveRecipeToCloud(userRef.current, result);
              setActiveRecipeId(result.id);
              setCurrentView('recipe-detail');
              showNotification("Recept toegevoegd!");
          } else {
              throw new Error("Scan mislukt");
          }
      } catch (err) {
          setRecipes(prev => prev.filter(r => r.id !== tempId));
          showNotification("Kon recept niet lezen.");
      } finally {
          setProcessingStatus('idle');
      }
  };

  const handleAddRecipe = async (r: Recipe) => {
      await saveRecipeToCloud(userRef.current, r);
      showNotification("Recept opgeslagen!");
  };

  const handleUpdateRecipe = async (updated: Recipe) => {
      await saveRecipeToCloud(userRef.current, updated);
  };

  const handleDeleteRecipe = async (id: string) => {
      await deleteRecipeFromCloud(userRef.current, id);
      showNotification("Recept verwijderd.");
  };

  const handleCategorizeList = async () => {
      const list = getActiveList();
      // Alleen producten zonder categorie; de rest is al gesorteerd
      const itemsToCategorize = list.items.filter(i => !i.checked && !i.category);
      if (itemsToCategorize.length === 0) return;

      try {
          // Gasten: alleen lokale sortering. Ingelogd: onbekende producten via AI.
          const categories = await categorizeItems(itemsToCategorize.map(i => ({ id: i.id, name: i.name })), !!user);
          
          const updatedItems = list.items.map(item => {
              const catMatch = categories.find(c => c.id === item.id);
              if (catMatch) {
                  return { ...item, category: catMatch.category };
              }
              return item;
          });

          updateListInCloud({ ...list, items: updatedItems });
          showNotification("Lijst slim gesorteerd!");
      } catch (error) {
          console.error("Categorization failed", error);
          showNotification("Kon lijst niet sorteren.");
      }
  };

  const handleInvitationResponse = async (invitation: Invitation, accept: boolean) => {
      if(!user) return;
      await respondToInvitation(user, invitation, accept);
      setShowInviteModal(false);
      showNotification(accept ? `Lijst "${invitation.listName}" toegevoegd!` : "Uitnodiging geweigerd");
  };

  if (!isDataLoaded) return <div className="h-screen w-full flex items-center justify-center"><span className="animate-spin material-symbols-outlined text-4xl text-primary">sync</span></div>;

  return (
    <div className="relative w-full h-full min-h-screen">
      {showOnboarding && <OnboardingScreen onComplete={(u) => { 
          localStorage.setItem('supermarct-onboarding-completed', 'true');
          setShowOnboarding(false); 
          if(u) handleLogin(u); 
      }} />}

      <main className="h-full w-full bg-background-light dark:bg-background-dark">
        {currentView === 'shared-recipe' && sharedRecipe && (
            <SharedRecipeScreen 
                recipe={sharedRecipe}
                shareToken={sharedRecipeToken}
                onImport={() => handleImportSharedRecipe(sharedRecipe, user)}
                onCancel={() => {
                    setSharedRecipe(null);
                    setCurrentView('recipes');
                }}
                isImporting={isImportingShared}
            />
        )}
        {currentView === 'list' && (
            <ShoppingListScreen
                currentUser={user}
                onOpenSharedLink={handleOpenSharedLinkFromClipboard}
                activeList={getActiveList()}
                allLists={shoppingLists}
                onSwitchList={setActiveListId}
                onCreateList={(name) => {
                    const newList: ShoppingList = { 
                        id: `list-${Date.now()}`, 
                        name, 
                        ownerId: user?.id || 'guest',
                        ownerName: user?.name || 'Gast',
                        items: [], 
                        sharedWith: [],
                        accessIds: user ? [user.id] : []
                    };
                    setShoppingLists(prev => [...prev, newList]);
                    setActiveListId(newList.id);
                    saveListToCloud(user, newList);
                }}
                onUpdateListName={(id, name) => {
                    const list = shoppingLists.find(l => l.id === id);
                    if (list) updateListInCloud({ ...list, name });
                }}
                onDeleteList={(id) => {
                    if (shoppingLists.length > 1) deleteListFromCloud(user, id);
                }}
                toggleItem={(itemId) => {
                    const list = getActiveList();
                    updateListInCloud({ ...list, items: list.items.map(i => i.id === itemId ? { ...i, checked: !i.checked, checkedAt: !i.checked ? Date.now() : undefined } : i) });
                }} 
                deleteItem={(itemId) => {
                    const list = getActiveList();
                    updateListInCloud({ ...list, items: list.items.filter(i => i.id !== itemId) });
                }}
                updateItemName={(itemId, name) => {
                    const list = getActiveList();
                    updateListInCloud({ ...list, items: list.items.map(i => i.id === itemId ? { ...i, name, category: getInstantCategory(name) || undefined } : i) });
                }}
                addItems={(newItems) => {
                    const list = getActiveList();
                    const currentItems = [...list.items]; // Local copy to update sequentially

                    newItems.forEach(({name, quantity}) => {
                        const normalizedName = name.trim().toLowerCase();
                        const existingItemIndex = currentItems.findIndex(i => i.name.trim().toLowerCase() === normalizedName);

                        if (existingItemIndex !== -1) {
                            const existingItem = currentItems[existingItemIndex];
                            const updatedItem = {
                                ...existingItem,
                                name: name.trim(),
                                quantity: quantity || existingItem.quantity,
                                checked: false,
                                checkedAt: undefined
                            };
                            currentItems.splice(existingItemIndex, 1);
                            currentItems.unshift(updatedItem);
                        } else {
                            const newItem = { 
                                id: Date.now().toString() + Math.random().toString(36).substr(2, 9), 
                                name: name.trim(), 
                                quantity, 
                                checked: false,
                                // Direct een categorie als het product bekend is (geen AI nodig)
                                category: getInstantCategory(name) || undefined
                            };
                            currentItems.unshift(newItem);
                        }
                    });
                    
                    updateListInCloud({ ...list, items: currentItems });
                }}
                onCategorize={handleCategorizeList}
                isOnline={!!user && isFirebaseReady()}
                lastSynced={lastSynced}
                onShowNotification={showNotification}
            />
        )}
        {(currentView === 'recipes' || currentView === 'recipe-detail') && (
            <RecipeScreen 
                currentUser={user}
                recipes={recipes} 
                shoppingLists={shoppingLists}
                selectedRecipeId={activeRecipeId}
                onSelectRecipe={(id) => navigateTo(id ? 'recipe-detail' : 'recipes', id)}
                onBack={() => window.history.back()} 
                onAddIngredients={(ings, targetListId) => {
                    const list = targetListId 
                        ? (shoppingLists.find(l => l.id === targetListId) || getActiveList())
                        : getActiveList();
                    const currentItems = [...list.items];
                    ings.forEach(ing => {
                        const normalizedName = ing.name.trim().toLowerCase();
                        const existingIdx = currentItems.findIndex(i => i.name.trim().toLowerCase() === normalizedName);
                        if (existingIdx !== -1) {
                            const existing = currentItems[existingIdx];
                            currentItems[existingIdx] = { ...existing, quantity: ing.amount, checked: false, checkedAt: undefined };
                        } else {
                            currentItems.unshift({ id: Math.random().toString(), name: ing.name, quantity: ing.amount, checked: false });
                        }
                    });
                    updateListInCloud({ ...list, items: currentItems });
                    showNotification(`Ingrediënten toegevoegd aan "${list.name}"!`);
                }}
                onAddRecipe={handleAddRecipe}
                onUpdateRecipe={handleUpdateRecipe}
                onDeleteRecipe={handleDeleteRecipe}
                onToggleFavorite={(id) => {
                    const recipe = recipes.find(r => r.id === id);
                    if (recipe) handleUpdateRecipe({ ...recipe, isFavorite: !recipe.isFavorite });
                }}
                onShowNotification={showNotification}
                onProcessImage={handleProcessImage}
            />
        )}
        {currentView === 'coach' && (
            <CoachScreen 
                user={user}
                existingRecipes={recipes}
                onAddRecipe={handleAddRecipe}
                onSelectRecipe={(id) => navigateTo('recipe-detail', id)}
                onShowNotification={showNotification}
                // Props from Lifted State
                inventory={coachInventory}
                setInventory={setCoachInventory}
                isAnalyzing={isCoachAnalyzing}
                recommendation={coachRecommendation}
                setRecommendation={setCoachRecommendation}
                addedIngredients={coachAddedIngredients}
                isRefining={isCoachRefining}
                onAnalyze={handleCoachAnalyze}
                onPhotoAnalyze={handleCoachPhotoAnalyze}
                onRefine={handleCoachRefine}
                onAddIngredient={handleCoachAddIngredient}
            />
        )}
        {currentView === 'settings' && (
            <SettingsScreen 
                user={user} onLogin={handleLogin} onLogout={handleLogout}
            />
        )}
      </main>
      
      {/* Notifications */}
      {notification && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-top-4 duration-300 pointer-events-none w-max max-w-[90%]">
            <div className="bg-black/80 dark:bg-white/90 backdrop-blur-md text-white dark:text-black px-6 py-3 rounded-full shadow-xl flex items-center gap-3">
                <span className="material-symbols-outlined text-primary filled text-xl">info</span>
                <span className="text-sm font-bold truncate">{notification}</span>
            </div>
        </div>
      )}

      {/* Invitation Modal */}
      {showInviteModal && invitations.length > 0 && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center px-4">
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowInviteModal(false)}></div>
              <div className="bg-white dark:bg-[#1A2E22] w-full max-w-sm rounded-[32px] p-6 shadow-2xl relative z-10 animate-in zoom-in-95 duration-300">
                  <div className="flex flex-col items-center text-center mb-6">
                      <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4">
                          <span className="material-symbols-outlined text-4xl text-primary filled">mail</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-1">Nieuwe Uitnodiging</h3>
                      <p className="text-sm text-gray-500">
                          <strong>{invitations[0].senderName}</strong> wil de lijst <em>"{invitations[0].listName}"</em> met je delen.
                      </p>
                  </div>
                  <div className="flex gap-3">
                      <button 
                        onClick={() => handleInvitationResponse(invitations[0], false)}
                        className="flex-1 py-4 rounded-xl bg-gray-100 dark:bg-black/20 font-bold text-sm text-gray-600 dark:text-gray-300"
                      >
                          Weigeren
                      </button>
                      <button 
                        onClick={() => handleInvitationResponse(invitations[0], true)}
                        className="flex-1 py-4 rounded-xl bg-primary text-black font-bold text-sm shadow-lg shadow-primary/20"
                      >
                          Accepteren
                      </button>
                  </div>
                  {invitations.length > 1 && (
                      <p className="text-[10px] text-gray-400 text-center mt-4">En nog {invitations.length - 1} andere(n)</p>
                  )}
              </div>
          </div>
      )}

      {/* Received Shared List Modal */}
      <ReceiveSharedListModal
          isOpen={showReceiveSharedListModal}
          sharedData={receivedSharedList}
          currentUser={user}
          onAccept={handleAcceptSharedList}
          onClose={() => {
              setShowReceiveSharedListModal(false);
              setReceivedSharedList(null);
          }}
          onLogin={handleLogin}
      />
      
      {currentView !== 'recipe-detail' && currentView !== 'shared-recipe' && (
        <Navigation 
            currentView={currentView} 
            setView={(v) => navigateTo(v)} 
            isCoachAnalyzing={isCoachAnalyzing}
        />
      )}
    </div>
  );
}